// @ts-nocheck

import React, { useState, useCallback, useRef, useLayoutEffect, useMemo } from 'react';
import type { ElementRef } from 'react';
import styled, { css } from 'styled-components';

import { ReactComponent as Close } from '../../assets/svg/close.svg';
import { Target } from '../../client';
import { __ } from '../../i18n/l10n';
import { ZIndices } from '../../styled-components/dark/common';
import { type SpineAnimationId } from '../../types/animations';
import { WinModalBetslip } from '../../types/winModal';
import { pixelToEm } from '../../utils/common';
import WinModalAnimation from '../WinModalAnimation';
import WinModalBetSwipe from '../WinModalBetSwipe';

const StyledAnimation = styled(WinModalAnimation)`
  ${({ $height }) => css`
    min-height: ${$height}px;
  `}
`;

const Wrap = styled.div`
  position: relative;
  overflow: hidden;
  background-color: #000;
  transition: opacity 150ms ease-in-out;

  ${({ $height }) => css`
    height: ${$height}px;
  `}}
`;

const Footer = styled.div`
  box-sizing: border-box;
  padding: ${pixelToEm(20)}em;
  height: ${pixelToEm(100)}em;
  width: 100%;
  display: flex;
  background-color: #000;
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: ${ZIndices.navBar};

  @media screen and (max-width: 600px) {
    padding: ${pixelToEm(16)}em;
    height: ${pixelToEm(80)}em;
  }

  @media screen and (max-width: 400px) {
    padding: ${pixelToEm(10)}em;
    height: ${pixelToEm(70)}em;
  }
`;

const FooterButton = styled.button`
  &&& {
    font-family: BettingPoliticaExtraBold, sans-serif;
    text-transform: uppercase;
    display: inline-block;
    width: 100%;
    height: 100%;
    padding: 0;
    margin-right: ${pixelToEm(20)}em;
    vertical-align: middle;
    text-align: center;
    font-size: ${pixelToEm(20)}em;
    line-height: 1.2;
    border-radius: 0;
    outline: none;
    color: #333;
    background: ${({ theme }) => theme.common.colors.light};
    cursor: pointer;

    @media screen and (max-width: 600px) {
      font-size: ${pixelToEm(16)}em;
    }

    @media screen and (max-width: 400px) {
      font-size: ${pixelToEm(14)}em;
    }

    &:last-child {
      margin-right: 0;
    }

    ${({ $isDefault, theme }) => {
      if ($isDefault) {
        return css`
          background: ${theme.common.colors.secondary};
          font-weight: bold;
        `;
      }
    }}

    :disabled {
      opacity: 0.5;
    }
  }
`;

const ExitButton = styled.button`
  && {
    position: absolute;
    right: 0;
    top: 0;
    padding: ${pixelToEm(30)}em;
    background: transparent;
    pointer-events: all;
    z-index: ${ZIndices.navBar};

    @media screen and (max-width: 600px) {
      padding: ${pixelToEm(20)}em;
    }

    @media screen and (max-width: 400px) {
      padding: ${pixelToEm(10)}em;
    }
  }
`;

const CloseIcon = styled(Close)`
  width: ${pixelToEm(40)}em;
  height: ${pixelToEm(40)}em;
  color: ${({ theme }) => theme.common.colors.lightest};

  ${Target.__MOBILE__
    ? undefined
    : css`
        &:hover {
          color: #fff;
        }
      `}

  @media screen and (max-width: 600px) {
    width: ${pixelToEm(30)}em;
    height: ${pixelToEm(30)}em;
  }
`;

const PositionVertically = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  width: 100%;
  height: calc(100% - ${pixelToEm(100)}em);
`;

const ForceWidth = styled.div`
  width: 100%;
  & > div {
    width: 100%;
  }
`;

type WinModalContentProps = {
  totalAmount: string;
  animationTypes: Array<{
    bannerAnimationId: SpineAnimationId;
    backgroundAnimationId?: SpineAnimationId;
    badge?: unknown;
    fallback?: string;
  }>;
  betslips: Array<WinModalBetslip>;
  openBetshare: (betslipReference: string) => void;
  openHistory: (betslipReference: string) => void;
  onClose: () => void;
  height?: number;
};

const WinModalContent = ({
  totalAmount,
  animationTypes,
  betslips,
  onClose,
  openBetshare,
  openHistory,
  height = 0,
}: WinModalContentProps) => {
  const [currentBetslipIndex, setCurrentBetslipIndex] = useState<number>(0);
  const refSwipe = useRef<ElementRef<'div'> | null>(null);
  const refFooter = useRef<ElementRef<'div'> | null>(null);
  const [animationHeight, setAnimationHeight] = useState<number>(0);
  const [swipeSize, setSwipeSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const currentAnimationType = useMemo(
    () => animationTypes[currentBetslipIndex] ?? {},
    [animationTypes, currentBetslipIndex],
  );
  const currentBackgroundAnimationId = useMemo(
    () => currentAnimationType.backgroundAnimationId,
    [currentAnimationType],
  );
  const currentBannerAnimationId = useMemo(() => currentAnimationType.bannerAnimationId, [currentAnimationType]);
  const currentBadge = useMemo(() => currentAnimationType.badge, [currentAnimationType]);
  const currentFallback = useMemo(() => currentAnimationType.fallback, [currentAnimationType]);
  const hasBetslips = betslips.length > 0;

  const goToBetshare = useCallback(() => {
    onClose();
    openBetshare(betslips[currentBetslipIndex].betslipReference);
  }, [betslips, currentBetslipIndex, onClose, openBetshare]);

  const goToHistory = useCallback(() => {
    onClose();
    openHistory(betslips[currentBetslipIndex].betslipReference);
  }, [betslips, currentBetslipIndex, onClose, openHistory]);

  const onSwipeLoad = useCallback((size) => setSwipeSize(size), []);

  useLayoutEffect(() => {
    const footerElement = refFooter.current;
    const swipeElement = refSwipe.current;
    if (swipeSize.height > 0 && swipeElement && footerElement) {
      const newHeight = Math.min(
        height - (swipeElement.offsetHeight + footerElement.offsetHeight),
        swipeElement.offsetWidth,
      );
      setAnimationHeight(newHeight);
    }
  }, [height, swipeSize]);

  return (
    <>
      <ExitButton onClick={onClose}>
        <CloseIcon />
      </ExitButton>
      <Wrap $height={height}>
        <PositionVertically>
          <ForceWidth>
            {animationHeight > 0 ? (
              <StyledAnimation
                $height={animationHeight}
                badge={currentBadge}
                backgroundAnimationId={currentBackgroundAnimationId}
                bannerAnimationId={currentBannerAnimationId}
                fallback={currentFallback}
                text={totalAmount}
              />
            ) : null}
            <WinModalBetSwipe
              ref={refSwipe}
              onLoad={onSwipeLoad}
              betslips={betslips}
              setIndex={setCurrentBetslipIndex}
              current={currentBetslipIndex}
            />
          </ForceWidth>
        </PositionVertically>
      </Wrap>
      <Footer ref={refFooter}>
        <FooterButton onClick={goToBetshare} $isDefault disabled={!hasBetslips}>
          {__('!action:Share')}
        </FooterButton>
        <FooterButton onClick={goToHistory} $isDefault disabled={!hasBetslips}>
          {__('View my bet')}
        </FooterButton>
      </Footer>
    </>
  );
};

export default WinModalContent;
